import { FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import Input from "../../../../components/Form/El/Input";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import {
  CREATE_USER_SETTING,
  UPDATE_USER_SETTING,
} from "../../Services/Mutations/Mutaions";
import { DataCollectionItem } from "../../../../types/AdminDashboard";
import { setNotification } from "../../../../helpers/cache";
import { USERS_COLLECTION_QUERY } from "../../Services/Queries/Queries";
import SearchBox from "../../../../components/Form/El/SearchBox";
import { User } from "../../../../types/User";
import EditOrCreateUserModal from "./Modal/EditOrCreateUserModal";

const AdminDashboardEditUserSettings: FC<{
  currentId?: number;
  isNeedSave: boolean;
  afterSave: (id: number) => void;
  labelWidth: string;
  inputWidth: string;
  currentDataCollectionItem?: DataCollectionItem;
  setIsSaveLoading: (state: boolean) => void;
  setDisabledSaveButton: (state: boolean) => void;
  onErrorSave: (error: ApolloError) => void;
}> = ({
  labelWidth,
  currentDataCollectionItem,
  inputWidth,
  isNeedSave,
  currentId,
  afterSave,
  setIsSaveLoading,
  setDisabledSaveButton,
  onErrorSave,
}) => {
  const clearForm = useCallback(() => {
    setCurrentValue("");
    setCurrentName("");
    setCurrentUser(undefined);
  }, []);

  const onAfterSave = useCallback((id: number) => {
    clearForm();
    afterSave(id || 0);
  }, []);

  const [updateItems] = useMutation(UPDATE_USER_SETTING, {
    onError: (error) => {
      setNotification([{ type: "ERROR", message: error?.message }]);
    },
    onCompleted: (data) => {
      const { updateUserSetting } = data;

      if (!updateUserSetting) return;

      const { id } = updateUserSetting;

      onAfterSave(id || 0);
    },
  });
  const [createItems] = useMutation(CREATE_USER_SETTING, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { createUserSetting } = data;

      if (!createUserSetting) return;

      const { id } = createUserSetting;

      onAfterSave(id || 0);
    },
  });
  const [getUsers] = useLazyQuery(USERS_COLLECTION_QUERY, {
    onError: (error) => {
      onErrorSave(error);
    },
    onCompleted: (data) => {
      const { users } = data;

      if (!users) return;

      setUsersList(users);
    },
  });

  const [currentValue, setCurrentValue] = useState("");
  const [currentName, setCurrentName] = useState("");
  const [currentUser, setCurrentUser] = useState<number | undefined>();
  const [usersList, setUsersList] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isShowAddEditUserModal, setIsShowAddEditUserModal] = useState("");

  useEffect(() => {
    if (!currentDataCollectionItem) return;

    const { value, user, name } = currentDataCollectionItem;

    if (value) {
      setCurrentValue(value);
    }

    if (name) {
      setCurrentName(name);
    }

    if (user) {
      setCurrentUser(user.id);
    }
  }, [currentDataCollectionItem]);

  const onGetUsers = useCallback(async () => {
    setIsLoadingUsers(true);

    await getUsers().finally(() => {
      setIsLoadingUsers(false);
    });
  }, []);

  useEffect(() => {
    onGetUsers();
  }, []);

  const usersForSelect = useMemo(() => {
    const newUserListForSelect = usersList.map((userItem) => {
      const userInUserList = usersList.find((user) => user.id === userItem.id);

      return {
        id: userInUserList?.id || 0,
        name: userInUserList?.userName || "",
      };
    });

    return newUserListForSelect.filter((user) => user.id !== currentUser);
  }, [usersList, currentUser]);

  const onSave = useCallback(async () => {
    setIsSaveLoading(true);
    const handleSaveById = async () => {
      if (currentId) {
        return await updateItems({
          variables: {
            id: currentId,
            userId: currentUser,
            name: currentName,
            value: currentValue,
          },
        });
      }
      return await createItems({
        variables: {
          id: currentId,
          userId: currentUser,
          name: currentName,
          value: currentValue,
        },
      });
    };

    await handleSaveById();
  }, [currentValue, currentId, currentUser, currentName]);

  useEffect(() => {
    if (isNeedSave) {
      onSave();
    }
  }, [isNeedSave]);

  useEffect(() => {
    const isValidForm = !!currentUser;
    setDisabledSaveButton(!isValidForm);
  }, [currentUser]);

  return (
    <Fragment>
      <div className="btn-group flex justify-end mb-1">
        <button
          className="btn btn-small btn-blue"
          onClick={() => {
            setIsShowAddEditUserModal("new");
          }}
        >
          <i className="icon-plus pr-1" /> Add New User
        </button>
        <button
          className="btn btn-small btn-blue"
          onClick={() => {
            setIsShowAddEditUserModal("edit");
          }}
          disabled={!currentUser}
        >
          <i className="icon-pencil pr-1" /> Edit Current User
        </button>
      </div>
      <SearchBox
        value={currentUser || ""}
        onChange={(value) => {
          if (typeof value === "number") {
            setCurrentUser(value);
          }
        }}
        options={usersForSelect}
        loading={isLoadingUsers}
        label="User"
        labelWidth={labelWidth}
        inputWidth={inputWidth}
        isIncorrectValue={!currentUser}
      />
      <Input
        value={currentName}
        onChange={(value) => {
          if (typeof value === "string") {
            setCurrentName(value);
          }
        }}
        name={`user-settings-name`}
        labelWidth={labelWidth}
        label="Name"
        placeholder="Name"
        inputWidth={inputWidth}
      />
      <Input
        value={currentValue}
        onChange={(value) => {
          if (typeof value === "string") {
            setCurrentValue(value);
          }
        }}
        name="user-settings-value"
        labelWidth={labelWidth}
        label="Value"
        placeholder="Value"
        inputWidth={inputWidth}
      />
      {!!isShowAddEditUserModal && (
        <EditOrCreateUserModal
          closeModal={() => {
            setIsShowAddEditUserModal("");
          }}
          afterSave={() => {
            onGetUsers();
          }}
          currentId={
            isShowAddEditUserModal === "edit" ? currentUser : undefined
          }
        />
      )}
    </Fragment>
  );
};

export default AdminDashboardEditUserSettings;
